'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button } from '@ui';
import { Input, Select } from '@ui';

/** First three titles from `development_process.lifecycle_items` (en/design-system.json) when no `stepLabels` prop. */
const DEFAULT_WIZARD_STEP_LABELS: [string, string, string] = [
  'UX research & discovery',
  'UX flows & UI design',
  'Content & data gathering',
];

export interface MultiStepModalWizardProps {
  open: boolean;
  onClose: () => void;
  /** Three labels for the stepper (e.g. first three titles from `development_process.lifecycle_items`). */
  stepLabels?: [string, string, string];
}

const MultiStepModalWizard: React.FC<MultiStepModalWizardProps> = ({
  open,
  onClose,
  stepLabels: stepLabelsProp,
}) => {
  const labels = stepLabelsProp ?? DEFAULT_WIZARD_STEP_LABELS;
  const stepsCount = labels.length;

  const [step, setStep] = useState(1);

  const nameRef = useRef<HTMLInputElement | null>(null);
  const phaseRef = useRef<HTMLSelectElement | null>(null);
  const clientRef = useRef<HTMLInputElement | null>(null);
  const targetsRef = useRef<HTMLInputElement | null>(null);
  const sitesRef = useRef<NodeListOf<HTMLInputElement> | null>(null);

  const nameErrRef = useRef<HTMLElement | null>(null);
  const phaseErrRef = useRef<HTMLElement | null>(null);
  const sitesErrRef = useRef<HTMLElement | null>(null);

  const showStep = useCallback((n: number) => setStep(n), []);

  const clearErr = (el?: HTMLElement | HTMLInputElement | HTMLSelectElement | null) => {
    if (!el) return;
    el.removeAttribute('aria-invalid');
    const next = (el as HTMLElement).nextElementSibling as HTMLElement | null;
    if (next && next.classList.contains('field-error')) {
      next.hidden = true;
      next.textContent = '';
    }
  };

  const showErr = (el: HTMLElement | null, msg: string, id?: string) => {
    el?.setAttribute?.('aria-invalid', 'true');
    if (id) {
      const err = document.getElementById(id);
      if (err) {
        err.hidden = false;
        err.textContent = msg;
      }
    }
  };

  const validateStep = useCallback((n: number) => {
    if (n === 1) {
      let ok = true;
      clearErr(nameRef.current);
      clearErr(phaseRef.current);
      if (!nameRef.current?.value.trim()) {
        showErr(nameRef.current as HTMLElement, 'Project name is required.', 'werr-name');
        ok = false;
      }
      if (!phaseRef.current?.value) {
        showErr(phaseRef.current as HTMLElement, 'Select a phase.', 'werr-phase');
        ok = false;
      }
      return ok;
    }
    if (n === 2) {
      if (sitesRef.current) {
        const checked = Array.from(sitesRef.current).filter((i) => i.checked);
        if (!checked.length) {
          if (sitesErrRef.current) {
            sitesErrRef.current.hidden = false;
            sitesErrRef.current.textContent = 'Pick at least one site.';
          }
          return false;
        }
        if (sitesErrRef.current) sitesErrRef.current.hidden = true;
        return true;
      }
      return false;
    }
    return true;
  }, []);

  type ReviewData = {
    name: string;
    phase: string;
    client: string;
    targets: string;
    sites: string;
  };

  const [review, setReview] = useState<ReviewData | null>(null);

  const buildReview = useCallback(() => {
    const name = nameRef.current?.value.trim() ?? '';
    const phase = phaseRef.current?.value ?? '';
    const client = clientRef.current?.value.trim() || '—';
    const targets = targetsRef.current?.value || '—';
    const sites = sitesRef.current
      ? Array.from(sitesRef.current)
          .filter((s) => s.checked)
          .map((i) => i.value)
          .join(', ')
      : '';
    setReview({ name, phase, client, targets, sites: sites || '—' });
  }, []);

  useEffect(() => {
    if (open) sitesRef.current = document.querySelectorAll('.wiz-site');
  }, [open]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const tgt = e.target as HTMLElement | null;
      const opener = tgt?.closest('[data-open-modal="#wizardModal"]') as HTMLElement | null;
      if (opener) {
        setTimeout(() => {
          setStep(1);
          const form = document.querySelector('#wizardModal form') as HTMLFormElement | null;
          form?.reset();
          if (nameErrRef.current) nameErrRef.current.hidden = true;
          if (phaseErrRef.current) phaseErrRef.current.hidden = true;
          if (sitesErrRef.current) sitesErrRef.current.hidden = true;
          setReview(null);
        }, 0);
      }
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const onBack = () => showStep(Math.max(1, step - 1));
  const onNext = () => {
    if (!validateStep(step)) return;
    const to = Math.min(stepsCount, step + 1);
    if (to === stepsCount) buildReview();
    showStep(to);
  };
  const onFinish = () => {
    if (!validateStep(stepsCount)) return;
    onClose();
    const maybeToast = window as unknown as {
      Toast?: (opts: { type: string; title: string; message: string }) => void;
    };
    if (maybeToast?.Toast) {
      maybeToast.Toast({
        type: 'success',
        title: 'Created',
        message: 'Study wizard completed.',
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='New Project'
      id='wizardModal'
      size='lg'
      actions={
        <>
          <Button label='Cancel' className='btn-ghost' onClick={onClose} />
          <div className='row'>
            <Button label='Back' className='btn-ghost' onClick={onBack} />
            <Button label='Next' className='btn-primary' onClick={onNext} />
            <Button label='Finish' className='btn-secondary' onClick={onFinish} />
          </div>
        </>
      }
    >
      <ol className='stepper-list' id='wizStepper' style={{ marginBottom: 12 }}>
        {labels.map((label, idx) => {
          const n = idx + 1;
          const isLast = n === stepsCount;
          return (
            <li
              key={`wiz-step-${idx}`}
              className={`stepper-step ${step === n ? 'is-active' : ''} ${
                !isLast && step > n ? 'is-complete' : ''
              }`}
            >
              <span className='dot'>{n}</span>
              <span>{label}</span>
            </li>
          );
        })}
      </ol>

      <form>
        <div hidden={step !== 1} className='wiz-step' data-step='1'>
          <div className='grid-3' style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <fieldset>
              <span>
                Project stage <b className='req'>*</b>
              </span>
              <Input ref={nameRef} id='wName' />
              <small className='field-error' id='werr-name' ref={nameErrRef} hidden />
            </fieldset>
            <fieldset>
              <span>
                Phase <b className='req'>*</b>
              </span>
              <Select
                ref={phaseRef}
                id='wPhase'
                options={[
                  { value: '', label: 'Select...', placeholder: true },
                  { value: 'phase-i', label: 'Phase I' },
                  { value: 'phase-ii', label: 'Phase II' },
                  { value: 'phase-iii', label: 'Phase III' },
                ]}
              />
              <small className='field-error' id='werr-phase' ref={phaseErrRef} hidden />
            </fieldset>
            <fieldset>
              <span>Client</span>
              <Input ref={clientRef} id='wSponsor' placeholder='TulioSolorzano' />
            </fieldset>
            <fieldset>
              <span>Target subjects</span>
              <Input ref={targetsRef} id='wTargets' placeholder='e.g., 120' />
              <small className='field-hint'>Optional</small>
            </fieldset>
          </div>
        </div>

        <div hidden={step !== 2} className='wiz-step' data-step='2'>
          <div className='row' style={{ gap: 8, flexWrap: 'wrap' }}>
            <label className='chip'>
              <input type='checkbox' className='wiz-site' value='North' defaultChecked /> North
            </label>
            <label className='chip'>
              <input type='checkbox' className='wiz-site' value='South' /> South
            </label>
            <label className='chip'>
              <input type='checkbox' className='wiz-site' value='East' /> East
            </label>
            <label className='chip'>
              <input type='checkbox' className='wiz-site' value='West' /> West
            </label>
            <label className='chip'>
              <input type='checkbox' className='wiz-site' value='Central' /> Central
            </label>
          </div>
          <small className='field-error' id='werr-sites' ref={sitesErrRef} hidden />
        </div>

        <div hidden={step !== 3} className='wiz-step' data-step='3'>
          <div className='card pad-16' id='wizReview'>
            {review ? (
              <div>
                <b>Review</b>
                <div className='mt-8'>
                  Name: <b>{review.name}</b>
                </div>
                <div className='mt-8'>
                  Phase: <b>{review.phase}</b>
                </div>
                <div className='mt-8'>
                  Client: <b>{review.client}</b>
                </div>
                <div className='mt-8'>
                  Target subjects: <b>{review.targets}</b>
                </div>
                <div className='mt-8'>
                  Sites: <b>{review.sites}</b>
                </div>
              </div>
            ) : (
              <div className='muted'>No review to show.</div>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default MultiStepModalWizard;
